<script lang="ts" setup>
import SalesLead from '@/classes/sales_classes/salesLead'
import type Trip from '@/classes/trip_classes/trip'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import { ActionTypes, MutationTypes } from '@/store/store_types'
import getAssetSrc from '@/utils/getAssetSrc'
import { getNiceDuration } from '@/utils/timeUtils'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import TripStatsColumnView from '../trips/statistics/TripStatsColumnView.vue'
import ElevatedBlockBtn from '../ui-elements/buttons/ElevatedBlockBtn.vue'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'

const store = useStore()
const { mobile } = useDisplay()

const { lead } = defineProps<{ lead: SalesLead | undefined }>()
const emit = defineEmits<{
  (e: 'contact-lead', lead: SalesLead): void
  (e: 'close'): void
}>()

function followUpPreCheck() {
  if (lead) {
    emit('contact-lead', lead)
  }
}

function src(asset: string) {
  return getAssetSrc(asset)
}

const niceDuration = (seconds: number): string => getNiceDuration(seconds, 'short')

const showTrip = (trip: Trip) => {
  store.dispatch(ActionTypes.showTrip, trip)
  store.commit(MutationTypes.setShowDashboard, false)
}

function imageSrc(vehicle: Vehicle): string {
  return (
    vehicle?.imageSrcPaths?.[0] ??
    vehicle?.evModel?.imageSrc ??
    getAssetSrc('car_images/placeholder_car.png')
  )
}
</script>
<template>
  <!-- lead page -->
  <v-card flat v-if="lead" class="mt-5">
    <!-- user details -->
    <v-card
      flat
      class="d-flex flex-column flex-md-row justify-center align-start justify-md-space-between align-md-center"
    >
      <div class="flex-grow-1 flex-shrink-1">
        <v-btn
          flat
          density="compact"
          icon
          variant="text"
          class="ml-2"
          color="pwt_azure"
          @click="emit('close')"
        >
          <v-icon> mdi-chevron-left </v-icon>
        </v-btn>
        <v-card-title>Enquiry from {{ lead.user.fullName ?? 'Anonymous' }}</v-card-title>
        <v-card-subtitle>{{ lead.user.email }}</v-card-subtitle>
      </div>
      <div v-if="mobile" class="flex-grow-1 flex-shrink-1 pl-4 mt-2" style="min-width: 100%">
        <ElevatedBlockBtn
          @click="followUpPreCheck"
          class="flex-grow-0 flex-shrink-0"
          :disabled="!!lead.contactedBy"
        >
          Follow up & email
        </ElevatedBlockBtn>
      </div>
      <ElevatedBtn
        v-else
        @click="followUpPreCheck"
        class="flex-grow-0 flex-shrink-0"
        :disabled="!!lead.contactedBy"
      >
        Follow up & email
      </ElevatedBtn>
    </v-card>
    <!-- trip details -->
    <v-card flat v-if="lead.trip" class="mt-10">
      <v-card-title>
        Trip to {{ lead.trip.destination?.name }}
        {{ lead.trip.vehicle?.evModel ? ' in ' + lead.trip.vehicle.evModel.model : '' }}
      </v-card-title>
      <v-card-subtitle
        class="d-flex align-center"
        style="cursor: pointer"
        @click="showTrip(lead.trip)"
      >
        <img
          :src="src('icons/Pin.svg')"
          :width="24"
          :height="24"
          class="flex-grow-0 flex-shrink-0 mr-2"
        />
        <div class="flex-grow-1 flex-shrink-1 text-decoration-underline text-truncate">
          {{ lead.trip.destination?.address }}
        </div>
      </v-card-subtitle>
      <v-card-text>
        <ul style="list-style: none; padding-left: unset">
          <li>{{ niceDuration(lead.trip.itinerary.totalTravelTime) }} Total driving time</li>
          <li>{{ Math.round(lead.trip.itinerary.totalDrivingDistance / 1000) }} Km driven</li>
          <li>{{ lead.trip.numberOfScheduledStops }} of planned stops along the way</li>
          <li>{{ lead.trip.chargingStopCDBIDs.length }} of additional charging stops</li>
        </ul>
      </v-card-text>
    </v-card>
    <!-- flowed up by details -->
    <v-card
      v-if="lead.contactedBy"
      flat
      class="ma-1 ml-4 mt-10 py-2 px-4 d-flex align-center"
      elevation="3"
      rounded="lg"
      width="250px"
    >
      <v-avatar color="primary" class="mr-2">
        <img
          v-if="lead.contactedBy?.driver?.profilePicSrc"
          :src="lead.contactedBy?.driver?.profilePicSrc"
          width="100%"
          height="auto"
        />
        <v-icon v-else dark> mdi-account-circle </v-icon>
      </v-avatar>
      <span class="text-truncate">
        {{ 'Followed up by ' + lead.contactedBy.firstName }}
      </span>
    </v-card>
    <!-- flowed up by details: Empty state placeholder -->
    <v-card
      v-else
      flat
      class="ma-1 ml-4 mt-10 py-2 px-4 d-flex align-center"
      elevation="3"
      rounded="lg"
      width="250px"
    >
      <v-icon color="pwt_light_grey" class="mr-2"> custom:markEmailUnread </v-icon> Awaiting
      response
    </v-card>
    <!-- top 5 recommendations -->
    <v-card flat v-if="lead.trip && lead.recommendedVehicles.length" class="mt-10">
      <v-card-title>
        Top
        {{ lead.recommendedVehicles.length < 5 ? lead.recommendedVehicles.length : 5 }}
        Recommendations
      </v-card-title>
      <v-slide-group class="bg-transparent mb-7 mx-n5 mx-md-0">
        <v-slide-group-item v-for="(item, index) in lead.recommendedVehicles" :key="index">
          <v-card
            elevation="4"
            width="150px"
            class="ma-2"
            :class="{ 'ml-7': index === 0, 'mr-7': index === lead.recommendedVehicles.length - 1 }"
            rounded="lg"
            color="pwt_snow"
          >
            <v-card-text class="pt-0">
              <div class="rounded-lg ma-1 overflow-hidden" style="height: 50%; width: 100%">
                <v-img
                  contain
                  :src="imageSrc(item)"
                  aspect-ratio="1"
                  width="100%"
                  height="100%"
                ></v-img>
              </div>
              <v-row no-gutters style="font-size: 16px !important">
                {{ item.licensePlate ?? item.name ?? item.evModel?.name ?? 'sale vehicle' }}
              </v-row>
              <v-row no-gutters v-if="item.licensePlate" style="font-size: 12px !important">
                {{ item.evModel?.name ?? 'combustion vehicle' }}
              </v-row>
            </v-card-text>
          </v-card>
        </v-slide-group-item>
      </v-slide-group>
    </v-card>
    <!-- top 5 recommendations: Empty state placeholder -->
    <v-card flat v-else-if="lead.trip" class="mt-10">
      <v-card-title>Top Recommendations</v-card-title>
      <v-card-text>There are no recommendations for this trip</v-card-text>
    </v-card>
    <!-- comparison -->
    <v-card flat v-if="lead.trip" class="mt-10">
      <v-row no-gutters>
        <v-col cols="12" md="6">
          <v-card-title> Current vehicle </v-card-title>
          <TripStatsColumnView :displayed-comparison-data="lead.trip.currentVehiclesComparison" />
        </v-col>
        <v-col cols="12" md="6">
          <v-card-title> Comparison vehicle </v-card-title>
          <TripStatsColumnView :displayed-comparison-data="lead.comparisonData" />
        </v-col>
      </v-row>
    </v-card>
    <!-- comparison: Empty state placeholder -->
    <v-card flat v-else-if="lead.trip"></v-card>
  </v-card>
  <!-- Empty state placeholder -->
  <v-card flat v-else></v-card>
</template>
